// ============================================================
// LIFE SIGNS (Squad Mode stage 8, board 10i, ruled 2026-10-23) - what a
// kill-team brother's Solo lane draws: his HEART as a wide ECG and his MIND
// as a narrow EEG over it, each read off what the plate already knows (the
// health band, the conditions, critical damage, frenzy). Derived, never
// stored. Named `lifeSigns` because `vitalsOf` (wounds or magnitude) is
// taken (D19).
//
// Every condition that touches the heart brings a real rhythm. Combining:
//   colour  = the health band, always (the caller paints it);
//   rhythm  = the most severe cardiac one (HEART_ORDER);
//   rate    = the fastest pull, then held under the slowest brake;
//   voltage = the lowest; mind = the most severe band (MIND_ORDER) at the
//   widest spread; Haywire and On Fire lay their artefacts over anything.
// A hallucinating brother's lane shows the intensity, never the vision: it
// tells no viewer more than the condition's chip does.
//
// Pure. Zero React dependency.
// ============================================================
import { parseHallucination } from '../hallucinationCode';
import { condType } from '../conditions';
import { parseDrainCode } from '../statDrains';
import type { PlateBand } from './plateBand';

export type Rhythm =
  | 'asystole'
  | 'vf'
  | 'bradyRed'
  | 'shock'
  | 'avBlock2'
  | 'ectopics'
  | 'svt'
  | 'hypovolaemia'
  | 'pac'
  | 'tremorTachy'
  | 'brady'
  | 'bradyBlock1'
  | 'sinus';
export type Wave =
  | 'flat'
  | 'fading'
  | 'delta'
  | 'burstSuppression'
  | 'chaos'
  | 'theta'
  | 'gamma'
  | 'flutter'
  | 'fearSpikes'
  | 'eyesClosedAlpha'
  | 'alpha'
  | 'beta';
type HeartArtefact = 'tremor' | 'mains';
type MindArtefact = 'painSpikes' | 'digitalSteps' | 'silenced';

export interface LifeSigns {
  band: PlateBand;
  heart: { rhythm: Rhythm; bpm: number; amp: number; st: number; artefacts: HeartArtefact[] };
  /** The second heart's trace: an Astartes' Critical damage wakes it. */
  second: boolean;
  /** `trains`: a power used, its spike trains this many spikes long; 0 for none (or silenced by Nullifying). */
  mind: { wave: Wave; spread: number; ghost: boolean; artefacts: MindArtefact[]; trains: number };
}

/** Most severe first: the rhythm drawn is the first any source brings. */
const HEART_ORDER: Rhythm[] = [
  'asystole',
  'vf',
  'bradyRed',
  'shock',
  'avBlock2',
  'ectopics',
  'svt',
  'hypovolaemia',
  'pac',
  'tremorTachy',
  'bradyBlock1',
  'brady',
  'sinus',
];
const MIND_ORDER: Wave[] = [
  'flat',
  'fading',
  'delta',
  'burstSuppression',
  'chaos',
  'theta',
  'gamma',
  'flutter',
  'fearSpikes',
  'eyesClosedAlpha',
  'alpha',
  'beta',
];

/** What one source does: a rhythm, a pull or a brake on the rate, a voltage, a wave and its spread, artefacts. */
interface Sign {
  rhythm?: Rhythm;
  pull?: number;
  brake?: number;
  amp?: number;
  st?: number;
  wave?: Wave;
  spread?: number;
  ghost?: boolean;
  heartArt?: HeartArtefact;
  mindArt?: MindArtefact;
  /** A power used: spike trains this many spikes long. */
  trains?: number;
}

/** The health band's own heart and mind: NSR 65, tachy 98, then ST depression deepening, red at Critical. */
const BAND: Record<PlateBand, Sign> = {
  full: { rhythm: 'sinus', pull: 65, wave: 'beta', spread: 1 },
  high: { rhythm: 'sinus', pull: 98, wave: 'beta', spread: 1.2 },
  med: { rhythm: 'sinus', pull: 116, st: 0.15, wave: 'beta', spread: 1.4 },
  low: { rhythm: 'sinus', pull: 124, st: 0.3, wave: 'beta', spread: 1.6 },
  crit: { rhythm: 'sinus', pull: 132, st: 0.45, wave: 'beta', spread: 1.8 },
  unknown: { rhythm: 'sinus', pull: 65, wave: 'beta', spread: 1 },
  dead: { rhythm: 'asystole', brake: 0, wave: 'flat', spread: 0 },
};

/** Each condition code that touches the body's signs (COND_NAMES codes; `Hl` and `Dn:` are read apart). */
const BY_CODE: Record<string, Sign> = {
  K: { rhythm: 'asystole', brake: 0, wave: 'flat', spread: 0 },
  Dy: { rhythm: 'vf', wave: 'fading', spread: 0.5 },
  U: { rhythm: 'bradyRed', brake: 44, wave: 'delta', spread: 1.6 },
  BLS: { rhythm: 'shock', pull: 140, amp: 0.45, wave: 'theta', spread: 1.6 },
  BL: { rhythm: 'hypovolaemia', pull: 118, amp: 0.65, wave: 'theta', spread: 1.2 },
  S: { rhythm: 'avBlock2', wave: 'burstSuppression', spread: 1.4 },
  Pn: { rhythm: 'pac', pull: 104, wave: 'flutter', spread: 1.5 },
  Fi: { rhythm: 'tremorTachy', pull: 120, heartArt: 'tremor', mindArt: 'painSpikes', spread: 1.6 },
  B: { wave: 'eyesClosedAlpha', spread: 1 },
  Hw: { heartArt: 'mains', mindArt: 'digitalSteps' },
  Pa: { rhythm: 'brady', brake: 50 },
  Af: { brake: 58, spread: 0.8 },
  Unw: { brake: 60, wave: 'alpha', spread: 0.9 },
};

/**
 * Codes that touch no sign - not physiological, or the plate says them already. Every COND_NAMES code is here or in
 * BY_CODE (the completeness test holds the line: a new condition must be placed).
 */
export const NO_SIGN = [
  'D', 'P', 'NS', 'Sn', 'Dr', 'Rg', 'Dsm', 'Ah', 'Ft', 'Ds', 'Gd', 'Ao', 'Ov', 'Hpl', 'Cr', 'De', 'Rn', 'Ta', 'Dg',
  'Immob', 'WepDest', 'EngDmg', 'HullBr', 'CrewHit', 'Disabled', 'Explodes',
] as const; // prettier-ignore

/** Fatigue: a mild tachycardia and a wider alpha per level. */
function fatigueSign(code: string): Sign | null {
  const m = code.match(/^F(\d+)?$/);
  if (!m) return null;
  const level = m[1] ? parseInt(m[1], 10) : 1;
  return { pull: 70 + level * 8, wave: 'alpha', spread: 1 + level * 0.15 };
}

/** Hallucinating: theta widening with the intensity; from 7, palpitations, chaos and a drifting ghost. */
function hallucinationSign(code: string): Sign | null {
  const h = parseHallucination(code);
  if (!h) return null;
  if (h.intensity >= 7) return { pull: 112, wave: 'chaos', spread: 1.4 + h.intensity * 0.1, ghost: true };
  return { wave: 'theta', spread: 1 + h.intensity * 0.12 };
}

/** The drains: Slowing slows the heart; Withering lowers its voltage; Numbing, Cowing and Nullifying are the mind's. */
const DRAIN_SIGNS: Record<string, Sign> = {
  agi: { rhythm: 'bradyBlock1', brake: 54, wave: 'theta', spread: 1 },
  tgh: { amp: 0.6 },
  wsbs: { wave: 'theta', spread: 1.1 },
  wp: { wave: 'fearSpikes', spread: 1.3 },
  psy: { mindArt: 'silenced' },
  str: {},
};

function drainSign(code: string): Sign | null {
  const d = parseDrainCode(code);
  return d ? (DRAIN_SIGNS[d.char] ?? null) : null;
}

/** What one condition code brings; null for one that touches nothing. */
function signOf(code: string): Sign | null {
  // By the condition's TYPE: a timed one carries its rounds ('S2', 'Dy3', 'Hw2' - the stage 8 review).
  return BY_CODE[condType(code)] ?? fatigueSign(code) ?? hallucinationSign(code) ?? drainSign(code);
}

const firstOf = <T>(order: readonly T[], present: readonly (T | undefined)[]): T | undefined =>
  order.find((o) => present.includes(o));

/** Every value one field of the signs takes (the ones that set it). */
const valuesOf = <K extends keyof Sign>(signs: readonly Sign[], key: K): NonNullable<Sign[K]>[] =>
  signs.flatMap((s) => (s[key] === undefined ? [] : [s[key] as NonNullable<Sign[K]>]));

function heartOf(signs: readonly Sign[]): LifeSigns['heart'] {
  const brakes = valuesOf(signs, 'brake');
  const rate = Math.max(0, ...valuesOf(signs, 'pull'));
  return {
    rhythm: firstOf(HEART_ORDER, valuesOf(signs, 'rhythm')) ?? 'sinus',
    bpm: brakes.length ? Math.min(rate, ...brakes) : rate,
    amp: Math.min(1, ...valuesOf(signs, 'amp')),
    st: Math.max(0, ...valuesOf(signs, 'st')),
    artefacts: [...new Set(valuesOf(signs, 'heartArt'))],
  };
}

function mindOf(signs: readonly Sign[]): LifeSigns['mind'] {
  const wave = firstOf(MIND_ORDER, valuesOf(signs, 'wave')) ?? 'beta';
  // The flat line is its own width: nothing widens a mind that has stopped.
  return {
    wave,
    spread: wave === 'flat' ? 0 : Math.max(0, ...valuesOf(signs, 'spread')),
    ghost: signs.some((s) => s.ghost),
    artefacts: [...new Set(valuesOf(signs, 'mindArt'))],
    // Nullifying silences a psyker's trains (the rulings' table).
    trains: valuesOf(signs, 'mindArt').includes('silenced') ? 0 : Math.max(0, ...valuesOf(signs, 'trains')),
  };
}

/**
 * A passing moment the lane shows for a few seconds (D21): a failed Fear test; a power used - a Force or Warp attack,
 * until psychic powers have a mark of their own (memory psyker-moment-is-a-stand-in, 2026-10-24).
 */
export type Moment = 'fear' | 'psy';

interface Body {
  band: PlateBand;
  conditions: readonly string[];
  criticalDamage?: readonly string[];
  frenzied?: boolean;
  /** Moments playing now: a failed Fear test throws a burst of spikes and a rate jump; a power, spike trains. */
  moments?: readonly Moment[];
  /** His Psy Rating: how long a power's spike trains run (1 to 10). */
  psyRating?: number;
}

const FEAR: Sign = { pull: 150, wave: 'fearSpikes', spread: 1.8 };

/** A moment's sign: a power's trains as long as his Psy Rating, at least one spike and at most ten. */
const momentSign = (m: Moment, psyRating = 0): Sign =>
  m === 'fear' ? FEAR : { pull: 105, trains: Math.min(10, Math.max(1, Math.round(psyRating))) };

/** His life signs, as his plate's lane draws them. */
export function lifeSignsOf(body: Body): LifeSigns {
  const signs = [
    BAND[body.band],
    ...body.conditions.flatMap((c) => signOf(c) ?? []),
    ...(body.criticalDamage?.length ? [{ rhythm: 'ectopics' as const }] : []),
    ...(body.frenzied ? [{ rhythm: 'svt' as const, pull: 190, wave: 'gamma' as const, spread: 1.8 }] : []),
    ...(body.moments ?? []).map((m) => momentSign(m, body.psyRating)),
  ];
  return { band: body.band, heart: heartOf(signs), second: !!body.criticalDamage?.length, mind: mindOf(signs) };
}
